import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Hook to determine and convert distance units based on user locale
 * US English uses miles, all other locales use kilometers
 * 
 * @returns {Object} Distance unit utilities
 * @returns {string} unit - 'mi' or 'km'
 * @returns {string} unitFull - 'miles' or 'kilometers'
 * @returns {Function} convertFromKm - Converts km to appropriate unit
 * @returns {Function} formatDistance - Formats distance with unit
 * @returns {boolean} isImperial - Whether using imperial system
 */
export const useDistanceUnit = () => {
  const { i18n, t } = useTranslation()
  
  const distanceConfig = useMemo(() => {
    const locale = i18n.language || 'en'
    
    // US English uses imperial (miles), all others use metric (kilometers)
    const isImperial = locale === 'en' || locale === 'en-US'
    
    // Conversion factor: 1 km = 0.621371 miles
    const KM_TO_MILES = 0.621371
    
    /**
     * Convert kilometers to the appropriate unit based on locale
     * @param {number} km - Distance in kilometers
     * @param {number} decimals - Number of decimal places (default: 1)
     * @returns {number} Distance in appropriate unit
     */
    const convertFromKm = (km, decimals = 1) => {
      if (!km || isNaN(km)) return 0
      const value = isImperial ? km * KM_TO_MILES : km
      return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
    }
    
    /**
     * Convert miles to kilometers (for backend API)
     * @param {number} miles - Distance in miles
     * @returns {number} Distance in kilometers
     */
    const convertToKm = (miles) => {
      if (!miles || isNaN(miles)) return 0
      return isImperial ? miles / KM_TO_MILES : miles
    }
    
    /**
     * Format distance with appropriate unit
     * @param {number} km - Distance in kilometers
     * @param {boolean} abbreviated - Use abbreviated unit (default: true)
     * @param {number} decimals - Number of decimal places (default: 1)
     * @returns {string} Formatted distance string
     */
    const formatDistance = (km, abbreviated = true, decimals = 1) => {
      const value = convertFromKm(km, decimals)
      const unit = abbreviated 
        ? t('unit.distanceAbbr', isImperial ? 'mi' : 'km')
        : t('unit.distance', isImperial ? 'miles' : 'kilometers')
      return `${value} ${unit}`
    }
    
    return {
      unit: isImperial ? 'mi' : 'km',
      unitFull: isImperial ? 'miles' : 'kilometers',
      convertFromKm,
      convertToKm,
      formatDistance,
      isImperial,
    }
  }, [i18n.language, t])
  
  return distanceConfig
}
